import { type ApiClient, ApiError, createApiClient } from '@nono/contract';
import { QueryClient } from '@tanstack/react-query';

/*
 * The ID token lives in memory only (ADR-011): never localStorage, never a
 * cookie. CSP is the XSS control, not token storage. On reload the user
 * re-authenticates silently via GIS auto-select.
 */
let authToken: string | null = null;
export function setAuthToken(token: string | null): void {
  authToken = token;
}
export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Same-origin by default: dev proxies /api → the real backend, prod serves the
 * SPA under the same host. Every response is Zod-parsed inside the client, so a
 * server-side shape drift throws here as a typed error rather than rendering
 * wrong data.
 */
/**
 * DEV ONLY — preview authed pages without a Google popup. With
 * `VITE_ADMIN_TOKEN=… pnpm dev:proxied` the client talks to the Bearer mirror
 * (/admin/*) instead of the Google-gated /api/dashboard/*, so a page can be
 * LOOKED at while it is being designed. Never reachable in a production build:
 * import.meta.env.DEV is statically false there and the branch is dropped.
 */
const devAdminToken = import.meta.env.DEV
  ? ((import.meta.env.VITE_ADMIN_TOKEN as string | undefined) ?? null)
  : null;

export const api: ApiClient = devAdminToken
  ? createApiClient({ baseUrl: '/admin', getToken: () => devAdminToken })
  : createApiClient({ getToken: () => authToken });

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 60 * 1000, // 15m — daily metrics change at most once/day
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          return false; // auth failures never retry
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

export { ApiError };
